'use client';

import { useFirebaseAuth } from '@/components/auth/FirebaseAuthProvider';
import HeaderAuthButtons from '@/components/auth/HeaderAuthButtons';
import ThemeToggle from '@/components/ThemeToggle';
import { primaryNavigationLinks } from '@/lib/site';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

export default function SiteHeader() {
  const { user, isLoading } = useFirebaseAuth();
  const pathname = usePathname();
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // The nav links point at heavy pages (/reference alone is ~670KB of RSC), and
  // they sit in the viewport on EVERY page — so Next's default viewport prefetch
  // pulled ~2MB of RSC in the background of every load, starving the actual tool
  // chunk. Prefetch OFF + warm on intent (hover/focus/touch) keeps navigation
  // instant without the per-page tax.
  const warm = (href: string) => router.prefetch(href);
  const accountHref = isLoading ? '/account' : user ? '/account' : '/login';
  const accountLabel = isLoading ? 'Account' : user ? 'Account' : 'Log in';

  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  return (
    <header className={`site-header ${isMenuOpen ? 'site-header-open' : ''}`}>
      <Link
        href="/"
        className="brandmark"
        aria-label="Cleanor Tools"
        prefetch={false}
        onMouseEnter={() => warm('/')}
        onFocus={() => warm('/')}
        onTouchStart={() => warm('/')}
      >
        <span className="brandmark-word brandmark-word-primary">Cleanor</span>
        <span className="brandmark-word brandmark-word-secondary">Tools</span>
      </Link>

      <nav className="site-nav desktop-nav" aria-label="Primary">
        {primaryNavigationLinks.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            prefetch={false}
            onMouseEnter={() => warm(item.href)}
            onFocus={() => warm(item.href)}
            onTouchStart={() => warm(item.href)}
          >
            {item.label}
          </Link>
        ))}
      </nav>

      <div className="site-header-actions desktop-actions">
        <ThemeToggle />
        <HeaderAuthButtons />
      </div>

      <button
        type="button"
        className="menu-toggle"
        aria-expanded={isMenuOpen}
        aria-controls="mobile-menu"
        aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        onClick={() => setIsMenuOpen((open) => !open)}
      >
        <span />
        <span />
      </button>

      <div id="mobile-menu" className={`mobile-menu ${isMenuOpen ? 'mobile-menu-open' : ''}`}>
        <nav className="mobile-nav" aria-label="Mobile primary">
          {primaryNavigationLinks.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              prefetch={false}
              onTouchStart={() => warm(item.href)}
              onMouseEnter={() => warm(item.href)}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="mobile-header-actions">
          <ThemeToggle />
          <HeaderAuthButtons />
          <Link className="soft-link mobile-account-link" href={accountHref}>
            {accountLabel}
          </Link>
        </div>
      </div>
    </header>
  );
}
